import React from "react";
import produce from "immer";
import moment from "moment";
import styled, { css } from "styled-components";
import { useMutation, queryCache } from "react-query";
import { Button, Row, Col, Checkbox, Popconfirm, Typography, Spin } from "antd";
import {
  CheckCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
  CloseOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { NotificationStateContext } from "context/UI/Notification/NotificationState";
import { apiAxios } from "services/HttpServices";
import EditDatelineModal from "components/job/index/modal/editDatelineModal";
import { CustomLinkButton } from "components/job/add/form/HotStampingRadio";

const { Text } = Typography;

const deleteIssue = async ({ jobId, issueId }) => {
  try {
    const res = await apiAxios.delete(`/issues/${issueId}/jobs/${jobId}`);
    return res.status;
  } catch (e) {
    throw e.response.data.message;
  }
};

const updateIssueStatus = async ({ value, jobId, issueId }) => {
  try {
    const res = await apiAxios.put(`/issues/${issueId}/jobs/${jobId}`, {
      resolved: value,
    });
    return res.data.data;
  } catch (e) {
    throw e.response.data.message;
  }
};

const IssueList = (props) => {
  // Component Props
  const { issues, status, setOpenNewIssueModal, jobId } = props;

  // Component State
  const [modalVisible, setModalVisible] = React.useState(false);
  const [selectedDateline, setSelectedDateline] = React.useState({});
  const [error, setError] = React.useState("");
  const [showCreateDateline, setShowCreateDateline] = React.useState(false);

  const {
    state: notificationState,
    setSuccessMessage,
    clearSuccessMessage,
    setLoading,
    finishLoading,
  } = React.useContext(NotificationStateContext);

  // Component Function
  const confirmDeleteIssue = async (jobId, issueId) => {
    setLoading(`deleteIssue-${issueId}`);
    try {
      await mutateDelete({ jobId, issueId });
      finishLoading(`deleteIssue-${issueId}`);
      setSuccessMessage("Succesfully deleted issue");
    } catch (error) {
      finishLoading(`deleteIssue-${issueId}`);
      console.log(error);
    }
  };

  const handleIssueStatus = async (value, jobId, issueId) => {
    if (value == true) {
      setLoading(`resolvedIssue-${issueId}`);
      try {
        await mutateIssueStatus({ value, jobId, issueId });
        finishLoading(`resolvedIssue-${issueId}`);
      } catch (error) {
        finishLoading(`resolvedIssue-${issueId}`);
        console.log(error);
      }
    } else if (value === false) {
      setLoading(`unResolvedIssue-${issueId}`);
      try {
        await mutateIssueStatus({ value, jobId, issueId });
        finishLoading(`unResolvedIssue-${issueId}`);
      } catch (error) {
        finishLoading(`unResolvedIssue-${issueId}`);
        console.log(error);
      }
    }
  };

  // Mutation
  const [mutateDelete] = useMutation(deleteIssue, {
    onSuccess: (_, variables) => {
      queryCache.setQueryData(
        ["job-detail", variables.jobId.toString()],
        function (oldData) {
          const issueIndex = oldData.issues.findIndex(
            (issue) => issue.id === variables.issueId
          );
          console.log("variables.issueId :>> ", variables.issueId);
          console.log("variables.jobId :>> ", variables.jobId);
          const updatedIssue = produce(oldData, (draft) => {
            draft.issues = draft.issues.filter(
              (issue) => issue.id !== variables.issueId
            );
          });
          console.log("updatedIssue :>> ", updatedIssue);
          return updatedIssue;
        }
      );
    },
    onError: (e) => {
      setError(e);
    },
  });

  const [mutateIssueStatus] = useMutation(updateIssueStatus, {
    onSuccess: (issue, variables) => {
      queryCache.setQueryData(
        ["job-detail", variables.jobId.toString()],
        function (oldData) {
          const issueIndex = oldData.issues.findIndex(
            (issue) => issue.id === variables.issueId
          );
          const updatedIssue = produce(oldData, (draft) => {
            draft.issues[issueIndex].resolved = variables.value;
          });
          return updatedIssue;
        }
      );
    },
    onError: (e) => {
      setError(e);
    },
  });

  return (
    <>
      <>
        <StyledChildRow>
          <Col span={24}>
            {issues?.map((issue, index) => {
              return (
                <StyledIssuesList
                  key={issue.id}
                  resolved={issue.resolved ? "true" : "false"}
                >
                  <Row style={{ width: "100%" }} align="middle">
                    <Col xs={16}>
                      <StyledDescriptionBox>
                        <span>{index + 1}.</span>
                        <p>{issue.description}</p>
                      </StyledDescriptionBox>
                    </Col>
                    <Col xs={8} align="end">
                      <StyledDateContainer>
                        <p>{moment(issue.created_at).format("DD MMM YYYY")}</p>
                        {!issue.resolved ? (
                          <CustomLinkButton
                            onClick={() =>
                              handleIssueStatus(true, jobId, issue.id)
                            }
                            className="button"
                            icon={
                              notificationState.loading[
                                `resolvedIssue-${issue.id}`
                              ] ? (
                                <SyncOutlined spin />
                              ) : (
                                <CheckCircleOutlined />
                              )
                            }
                          >
                            Resolved
                          </CustomLinkButton>
                        ) : (
                          <CustomLinkButton
                            onClick={() =>
                              handleIssueStatus(false, jobId, issue.id)
                            }
                            className="button"
                            icon={
                              notificationState.loading[
                                `unResolvedIssue-${issue.id}`
                              ] ? (
                                <SyncOutlined spin />
                              ) : (
                                <CloseOutlined />
                              )
                            }
                          >
                            Unresolved
                          </CustomLinkButton>
                        )}
                        <Popconfirm
                          title="Are you sure delete this dateline?"
                          onConfirm={() => confirmDeleteIssue(jobId, issue.id)}
                          onCancel={() => {
                            return;
                          }}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button
                            type="link"
                            size="small"
                            icon={<DeleteOutlined />}
                            loading={
                              notificationState.loading[
                                `deleteIssue-${issue.id}`
                              ]
                            }
                            css={`
                              color: ${(props) => props.theme.colors.grey[700]};
                              margin-left: ${(props) => props.theme.spacing[1]};
                            `}
                          />
                        </Popconfirm>
                      </StyledDateContainer>
                    </Col>
                  </Row>
                </StyledIssuesList>
              );
            })}
          </Col>

          <Col span={24}>
            <StyledIssuesList $add onClick={() => setOpenNewIssueModal(true)}>
              <StyledButton
                shape="circle"
                size="small"
                icon={<PlusOutlined />}
              />
              <ShallowText>Report new issue</ShallowText>
            </StyledIssuesList>
          </Col>
        </StyledChildRow>
        <EditDatelineModal
          modalVisible={modalVisible}
          selectedDateline={selectedDateline}
          handleCloseModal={() => {
            setModalVisible(false);
          }}
        />
      </>
    </>
  );
};

const StyledChildRow = styled(Row)`
  background-color: ${(props) => props.theme.colors.white};
  margin-top: ${(props) => props.theme.spacing[2]};
  ${(props) =>
    props.$newJob &&
    css`
      padding: ${(props) => props.theme.spacing[4]};
    `};
`;

const StyledIssuesList = styled.div`
  padding: ${(props) => props.theme.spacing[2]};
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${(props) => props.theme.colors.grey[200]};
  ${(props) =>
    props.$add &&
    css`
      border-bottom: none;
      cursor: pointer;
      padding-top: ${(props) => props.theme.spacing[3]};
      padding-bottom: ${(props) => props.theme.spacing[3]};
    `};

  ${(props) =>
    props.resolved === "true" &&
    css`
      color: ${(props) => props.theme.colors.grey[600]};
      background-color: ${(props) => props.theme.colors.grey[200]};
      font-style: italic;
      text-decoration: line-through;
    `};
`;

const StyledButton = styled(Button)`
  background: ${(props) => props.theme.colors.grey[300]};
  color: ${(props) => props.theme.colors.grey[600]};
  margin-right: ${(props) => props.theme.spacing[2]};
`;

const StyledDueDateContainer = styled.div`
  border-radius: ${(props) => props.theme.spacing[1]};
  display: flex;
  align-items: center;
  padding: 1px;
  margin-right: ${(props) => props.theme.spacing[2]};
  background-color: ${(props) =>
    props.variant === "good"
      ? props.theme.colors.green[200]
      : props.variant === "warning"
      ? props.theme.colors.yellow[200]
      : props.variant === "danger" && props.theme.colors.red[200]};
  border: 1px solid
    ${(props) =>
      props.variant === "good"
        ? props.theme.colors.green[400]
        : props.variant === "warning"
        ? props.theme.colors.yellow[400]
        : props.variant === "danger" && props.theme.colors.red[400]};
  .text {
    margin-right: 5px;
    font-size: 0.7rem;
    color: ${(props) =>
      props.variant === "good"
        ? props.theme.colors.green[700]
        : props.variant === "warning"
        ? props.theme.colors.yellow[700]
        : props.variant === "danger" && props.theme.colors.red[700]};
  }
  .icon {
    margin: 0 5px;
    color: ${(props) =>
      props.variant === "good"
        ? props.theme.colors.green[700]
        : props.variant === "warning"
        ? props.theme.colors.yellow[700]
        : props.variant === "danger" && props.theme.colors.red[700]};
  }
`;

const ShallowText = styled(Text)`
  color: ${(props) => props.theme.colors.grey[500]};
  font-size: ${(props) => props.theme.fontSize["sm"]};
`;

const StyledDescriptionBox = styled.div`
  display: flex;

  p {
    margin: 0;
    margin-left: ${(props) => props.theme.spacing[2]};
  }
`;

const StyledDateContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  p {
    margin: 0;
    margin-right: ${(props) => props.theme.spacing[2]};
    font-size: ${(props) => props.theme.fontSize["xs"]};
    color: ${(props) => props.theme.colors.grey[500]};
    font-style: italic;
  }
`;

export default IssueList;
